import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { t } from '../../i18n/i18n';
import './TermsViewer.scss';

/** The browser's own viewer, with its toolbar and side panel hidden, fitted to width. */
const VIEW = '#toolbar=0&navpanes=0&view=FitH';

/** How long a fullscreen request gets before the overlay stands in. */
const FULLSCREEN_DEADLINE = 700;

function ExpandIcon({ full }) {
  return full ? (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M8 3v5H3M12 3v5h5M8 17v-5H3M12 17v-5h5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path
        d="M3 7.5V3h4.5M17 7.5V3h-4.5M3 12.5V17h4.5M17 12.5V17h-4.5M3 3l5 5M17 3l-5 5M3 17l5-5M17 17l-5-5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The Terms and Conditions PDF, readable in place under step 1 of the checkout,
 * with a toggle that takes the document fullscreen. Fullscreen goes through the
 * Fullscreen API on the frame's own wrapper, so Esc and the browser's exit both
 * bring it back — the icon follows `fullscreenchange` rather than its own click.
 * If the API is refused, the frame covers the window instead (see `overlay`).
 *
 * `src` is the PDF's URL; "Open in a new tab" is the fallback for browsers
 * (mostly mobile) that won't render a PDF inside a frame.
 */
export default function TermsViewer({ src, title = 'Terms and Conditions' }) {
  const frame = useRef(null);
  const [native, setNative] = useState(false);
  // Where the Fullscreen API is refused — iOS Safari has none for a div, and
  // embedded browsers deny the permission — the frame covers the window instead.
  const [overlay, setOverlay] = useState(false);
  const full = native || overlay;

  useEffect(() => {
    const sync = () => {
      const on = document.fullscreenElement === frame.current;
      setNative(on);
      // A grant that lands after the deadline takes over from the overlay.
      if (on) setOverlay(false);
    };
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  // The overlay has no browser chrome to exit through, so Esc does it.
  useEffect(() => {
    if (!overlay) return undefined;
    const onKey = (event) => event.key === 'Escape' && setOverlay(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [overlay]);

  const toggle = async () => {
    if (overlay) return setOverlay(false);
    if (document.fullscreenElement) return document.exitFullscreen?.();
    if (!frame.current?.requestFullscreen) return setOverlay(true);

    // A refusal can also be silence — some embedded browsers never settle the
    // request at all — so it gets a deadline, not just a catch.
    const granted = await Promise.race([
      frame.current.requestFullscreen().then(
        () => true,
        () => false,
      ),
      new Promise((resolve) => setTimeout(() => resolve(false), FULLSCREEN_DEADLINE)),
    ]);
    if (!granted && !document.fullscreenElement) setOverlay(true);
    return undefined;
  };

  const label = full ? t('Exit fullscreen') : t('View fullscreen');
  const heading = t(title);

  const viewer = (
    <div
      className={`ta-terms__frame${native ? ' is-full' : ''}${overlay ? ' is-overlay' : ''}`}
      ref={frame}
    >
      <iframe className="ta-terms__doc" src={`${src}${VIEW}`} title={heading} />
      <button
        type="button"
        className="ta-terms__expand"
        onClick={toggle}
        aria-label={label}
        title={label}
      >
        <ExpandIcon full={full} />
      </button>
    </div>
  );

  return (
    <section className="ta-section ta-terms" aria-label={heading}>
      <div className="ta-terms__head">
        <h2 className="ta-section__title">{heading}</h2>
        <a className="ta-terms__open" href={src} target="_blank" rel="noreferrer">
          {t('Open in a new tab')}
        </a>
      </div>

      {/* The overlay goes to <body>: the checkout's sections are stacking
          contexts (their entrance animation), so a fixed frame left inside one
          would paint under the shell's rail. The page keeps a same-size blank
          in its place so nothing below jumps. */}
      {overlay ? (
        <>
          <div className="ta-terms__frame" aria-hidden="true" />
          {createPortal(viewer, document.body)}
        </>
      ) : (
        viewer
      )}
    </section>
  );
}
